import React, { useState } from "react"
import { useMenu, useLogout, useGetIdentity } from "@refinedev/core"
import { useLocation, Link } from "react-router-dom"
import {
  ChevronDown,
  LogOut,
  Menu,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { resolveIcon } from "./icon-map"
import { useFeatureEnabled } from "@/hooks/useFeatureEnabled"

interface AppSidebarProps {
  title?: React.ReactNode
  collapsed?: boolean
  onCollapse?: (collapsed: boolean) => void
}

function SidebarMenuItem({
  item,
  collapsed,
  depth = 0,
  siblings = [],
}: {
  item: any
  collapsed: boolean
  depth?: number
  siblings?: any[]
}) {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(() => {
    if (!item.children?.length) return false
    return item.children.some(
      (child: any) => child.route && pathname.startsWith(child.route)
    )
  })

  const Icon = resolveIcon(item.icon)
  // Check basic prefix match, then ensure no sibling has a longer (more specific) matching route
  const basicMatch =
    item.route && (pathname === item.route || pathname.startsWith(item.route + "/"))
  const isActive = basicMatch && !siblings.some(
    (s: any) => s.route && s.route !== item.route &&
      s.route.length > item.route.length &&
      (pathname === s.route || pathname.startsWith(s.route + "/"))
  )
  const hasChildren = item.children && item.children.length > 0

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => setOpen(!open)}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
            "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            collapsed && "justify-center px-2"
          )}
        >
          <Icon className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <>
              <span className="flex-1 text-left truncate">
                {item.label ?? item.name}
              </span>
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 shrink-0 transition-transform",
                  open && "rotate-180"
                )}
              />
            </>
          )}
        </button>
        {!collapsed && open && (
          <div className="ml-4 mt-0.5 space-y-0.5 border-l border-border pl-2">
            {item.children.map((child: any) => (
              <SidebarMenuItem
                key={child.key}
                item={child}
                collapsed={collapsed}
                depth={depth + 1}
                siblings={item.children}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  if (!item.route) return null

  const linkContent = (
    <Link
      to={item.route}
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
        isActive
          ? "bg-primary/10 text-primary font-medium"
          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        collapsed && "justify-center px-2"
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && (
        <span className="truncate">{item.label ?? item.name}</span>
      )}
    </Link>
  )

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {item.label ?? item.name}
        </TooltipContent>
      </Tooltip>
    )
  }

  return linkContent
}

function SidebarContent({
  collapsed,
  title,
}: AppSidebarProps & { collapsed: boolean }) {
  const { menuItems } = useMenu()
  // Default ON; the operator hides the InTutor tab per tenant via SaaSBOXX.
  const intutorVisible = useFeatureEnabled("lab_ui_help_intutor")
  const footfallVisible = useFeatureEnabled("platform_footfall")
  const { mutate: logout } = useLogout()
  const { data: user } = useGetIdentity<{
    name?: string
    email?: string
  }>()

  return (
    <TooltipProvider delayDuration={0}>
    <div className="flex h-full flex-col">
      {/* Logo / Title */}
      <div
        className={cn(
          "flex h-14 items-center border-b px-4",
          collapsed && "justify-center px-2"
        )}
      >
        {title ? (
          <div className={cn("truncate", collapsed && "hidden")}>{title}</div>
        ) : (
          <span
            className={cn(
              "text-sm font-bold text-primary truncate",
              collapsed && "hidden"
            )}
          >
            LabBOXX LIMS
          </span>
        )}
        {collapsed && (
          <span className="text-lg font-bold text-primary">L</span>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-2 py-2">
          <nav className="space-y-0.5">
            {menuItems
              .filter((item) => !(item.name === "help" && !intutorVisible))
              .filter((item) => !(item.name === "footfall" && !footfallVisible))
              .map((item) => (
              <SidebarMenuItem
                key={item.key}
                item={item}
                collapsed={collapsed}
              />
            ))}
          </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t p-2">
        {user && !collapsed && (
          <div className="px-3 py-2 text-xs text-muted-foreground truncate">
            {user.name || user.email}
          </div>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => logout()}
              className={cn(
                "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive",
                collapsed && "justify-center px-2"
              )}
            >
              <LogOut className="h-4 w-4 shrink-0" />
              {!collapsed && <span>Logout</span>}
            </button>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right" sideOffset={8}>
              Logout
            </TooltipContent>
          )}
        </Tooltip>
      </div>
    </div>
    </TooltipProvider>
  )
}

export function AppSidebar({ title, collapsed = false, onCollapse }: AppSidebarProps) {
  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          "hidden md:flex flex-col border-r bg-card transition-all duration-300",
          collapsed ? "w-[68px]" : "w-[260px]"
        )}
      >
        <SidebarContent
          collapsed={collapsed}
          title={title}
          onCollapse={onCollapse}
        />
      </aside>

      {/* Mobile sidebar (Sheet) */}
      <Sheet>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden fixed top-3 left-3 z-40"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[260px] p-0">
          <SidebarContent collapsed={false} title={title} />
        </SheetContent>
      </Sheet>
    </>
  )
}
